/**
 * Validação e parsing de códigos de barras brasileiros
 * Suporta:
 *  - Boleto bancário (FEBRABAN) — 47 dígitos numéricos ou linha digitável 47 dígitos
 *  - Contas de consumo/concessionárias (FEBRABAN) — 44 dígitos, produto 8
 */

export interface ResultadoValidacao {
  valido: boolean;
  tipo: 'boleto' | 'consumo' | 'desconhecido';
  valor: number;           // 0 = não identificado / livre
  vencimento: string | null; // ISO date string ou null
  beneficiario: string;
  banco: string;
  erros: string[];
}

// ─── Módulo 10 (da direita para esquerda — padrão boleto bancário) ───────────
function modulo10(numero: string): number {
  let soma = 0;
  let mult = 2;
  for (let i = numero.length - 1; i >= 0; i--) {
    let res = parseInt(numero[i], 10) * mult;
    if (res > 9) res = Math.floor(res / 10) + (res % 10);
    soma += res;
    mult = mult === 2 ? 1 : 2;
  }
  const resto = soma % 10;
  return resto === 0 ? 0 : 10 - resto;
}

// ─── Módulo 10 da esquerda para direita (usado em blocos de concessionárias) ─
function modulo10Esquerda(numero: string): number {
  let soma = 0;
  let mult = 2;
  for (let i = 0; i < numero.length; i++) {
    let res = parseInt(numero[i], 10) * mult;
    if (res > 9) res = Math.floor(res / 10) + (res % 10);
    soma += res;
    mult = mult === 2 ? 1 : 2;
  }
  const resto = soma % 10;
  return resto === 0 ? 0 : 10 - resto;
}

// ─── Módulo 11 ───────────────────────────────────────────────────────────────
function modulo11(numero: string, base = 9): number {
  let soma = 0;
  let mult = 2;
  for (let i = numero.length - 1; i >= 0; i--) {
    soma += parseInt(numero[i], 10) * mult;
    mult = mult >= base ? 2 : mult + 1;
  }
  const resto = soma % 11;
  if (resto === 0 || resto === 1) return 1;
  return 11 - resto;
}

// ─── Módulo 11 da esquerda para direita (usado em blocos de concessionárias) ─
function modulo11Esquerda(numero: string): number {
  let soma = 0;
  let mult = 2;
  for (let i = 0; i < numero.length; i++) {
    soma += parseInt(numero[i], 10) * mult;
    mult = mult > 9 ? 2 : mult + 1;
  }
  const resto = soma % 11;
  if (resto === 0 || resto === 1) return 0;
  return 11 - resto;
}

// ─── Tabela de bancos ─────────────────────────────────────────────────────────
const BANCOS: Record<string, string> = {
  '001': 'Banco do Brasil',
  '033': 'Santander',
  '041': 'Banrisul',
  '077': 'Banco Inter',
  '104': 'Caixa Econômica Federal',
  '208': 'BTG Pactual',
  '212': 'Banco Original',
  '237': 'Bradesco',
  '260': 'Nu Pagamentos (Nubank)',
  '290': 'PagBank',
  '341': 'Itaú',
  '389': 'Banco Mercantil do Brasil',
  '422': 'Banco Safra',
  '623': 'Banco Pan',
  '633': 'Banco Rendimento',
  '655': 'Banco Votorantim',
  '707': 'Banco Daycoval',
  '745': 'Citibank',
  '748': 'Sicredi',
  '756': 'Sicoob',
};

// ─── Tabela de concessionárias (produto 8) ────────────────────────────────────
const CONCESSIONARIAS: Record<string, string> = {
  '8': 'Conta de Consumo / Concessionária',
};

// ─── Converte linha digitável de boleto (47 dígitos) para código de barras (44) ──
function linhaDigitavelParaCodigoBarras(linha: string): string {
  // Formato: AAABC.CCCCX DDDDD.DDDDDY EEEEE.EEEEEZ K UUUUVVVVVVVVVV
  // Posições (0-indexed, sem espaços/pontos):
  // Campo 1: 0-9   → barras 0-3 + 19-24
  // Campo 2: 10-20 → barras 24-34
  // Campo 3: 21-31 → barras 34-44
  // Dígito verificador: 32
  // Vencimento+valor: 33-46
  const l = linha.replace(/\D/g, '');
  if (l.length !== 47) return l;

  const banco = l.substring(0, 3);
  const moeda = l.substring(3, 4);
  const campo1 = l.substring(4, 9);   // 5 dígitos do campo 1 (sem dígito verificador)
  const campo2 = l.substring(10, 20); // 10 dígitos do campo 2 (sem dígito verificador)
  const campo3 = l.substring(21, 31); // 10 dígitos do campo 3 (sem dígito verificador)
  const digitoGeral = l.substring(32, 33);
  const vencValor = l.substring(33, 47); // 14 dígitos: 4 venc + 10 valor

  return banco + moeda + digitoGeral + vencValor + campo1 + campo2 + campo3;
}

// ─── Converte linha digitável de conta de consumo (48 dígitos) para código de barras (44) ──
//
// Layout FEBRABAN real para linha digitável de concessionárias (48 dígitos):
//
//  Bloco 1 (12 dígitos): P  S  R  DV_B1  VVVVVVVV
//    pos 0   = Produto (8)
//    pos 1   = Segmento
//    pos 2   = Real/Valor (indica módulo de validação)
//    pos 3   = DV do bloco 1
//    pos 4-11= Valor (8 dígitos, em centavos, sem separador)
//
//  Bloco 2 (12 dígitos): CCCCCCCCCCC  DV_B2
//    pos 12-22 = Campo livre parte 1 (11 dígitos)
//    pos 23    = DV do bloco 2
//
//  Bloco 3 (12 dígitos): CCCCCCCCCCC  DV_B3
//    pos 24-34 = Campo livre parte 2 (11 dígitos)
//    pos 35    = DV do bloco 3
//
//  Bloco 4 (12 dígitos): CCCCCCCCCCC  DV_B4
//    pos 36-46 = Campo livre parte 3 (11 dígitos)
//    pos 47    = DV do bloco 4
//
// Código de barras (44 dígitos):
//   P(1) + S(1) + R(1) + DV_GERAL(1) + VALOR(11) + CAMPO_LIVRE(29)
//   VALOR = 3 zeros + 8 dígitos do bloco 1 (pos 4-11) = 11 dígitos
//   CAMPO_LIVRE = bloco2(11) + bloco3(11) + bloco4(7) = 29 dígitos
function linhaDigitavelConsumoParaCodigoBarras(linha: string): string {
  const l = linha.replace(/\D/g, '');
  if (l.length !== 48) return l;

  const produto   = l.substring(0, 1);  // pos 0
  const segmento  = l.substring(1, 2);  // pos 1
  const realValor = l.substring(2, 3);  // pos 2
  // pos 3 = DV bloco 1 — ignorado na montagem
  const valor8    = l.substring(4, 12); // pos 4-11 (8 dígitos de valor em centavos)

  const campoLivre1 = l.substring(12, 23); // pos 12-22 (11 dígitos)
  // pos 23 = DV bloco 2

  const campoLivre2 = l.substring(24, 35); // pos 24-34 (11 dígitos)
  // pos 35 = DV bloco 3

  const campoLivre3 = l.substring(36, 47); // pos 36-46 (11 dígitos)
  // pos 47 = DV bloco 4

  // Valor no código de barras = 11 dígitos (pad com 3 zeros à esquerda)
  const valorCodigo = '000' + valor8; // 11 dígitos

  // Campo livre total = 29 dígitos
  const campoLivre = campoLivre1 + campoLivre2 + campoLivre3.substring(0, 7); // 11+11+7=29

  // Dígito geral provisório = '0' (não validado para linha de 48 dígitos)
  return produto + segmento + realValor + '0' + valorCodigo + campoLivre;
}

// ─── Extrai data de vencimento do fator (dias desde 07/10/1997) ───────────────
function fatorParaData(fator: string): string | null {
  const f = parseInt(fator, 10);
  if (f === 0) return null; // sem vencimento
  const base = new Date('1997-10-07');
  base.setDate(base.getDate() + f);
  return base.toISOString().split('T')[0];
}

// ─── Valida boleto bancário (44 dígitos) ──────────────────────────────────────
function validarBoleto(codigo44: string): ResultadoValidacao {
  const erros: string[] = [];

  if (codigo44.length !== 44) {
    erros.push('Código de barras deve ter 44 dígitos.');
    return { valido: false, tipo: 'boleto', valor: 0, vencimento: null, beneficiario: '', banco: '', erros };
  }

  const banco = codigo44.substring(0, 3);
  const moeda = codigo44.substring(3, 4);
  const digitoVerificador = codigo44.substring(4, 5);
  const fatorVencimento = codigo44.substring(5, 9);
  const valorStr = codigo44.substring(9, 19);
  const campoLivre = codigo44.substring(19, 44);

  if (moeda !== '9') {
    erros.push('Moeda inválida (esperado 9 = Real).');
  }

  // Valida dígito verificador geral (módulo 11)
  const semDigito = codigo44.substring(0, 4) + codigo44.substring(5);
  const dvCalculado = modulo11(semDigito);
  if (String(dvCalculado) !== digitoVerificador) {
    erros.push('Dígito verificador inválido.');
  }

  const valor = parseInt(valorStr, 10) / 100;
  const vencimento = fatorParaData(fatorVencimento);
  const nomeBanco = BANCOS[banco] || `Banco ${banco}`;

  return {
    valido: erros.length === 0,
    tipo: 'boleto',
    valor,
    vencimento,
    beneficiario: nomeBanco,
    banco: nomeBanco,
    erros,
  };
}

// ─── Valida conta de consumo/concessionária (44 dígitos, produto 8) ───────────
function validarContaConsumo(codigo44: string, linhaOriginal48?: string): ResultadoValidacao {
  const erros: string[] = [];

  if (codigo44.length !== 44) {
    erros.push('Código deve ter 44 dígitos.');
    return { valido: false, tipo: 'consumo', valor: 0, vencimento: null, beneficiario: '', banco: '', erros };
  }

  const produto   = codigo44.substring(0, 1);
  const segmento  = codigo44.substring(1, 2);
  const realValor = codigo44.substring(2, 3); // 6=módulo10, 7=módulo10, 8=módulo11, 9=módulo11
  const digitoGeral = codigo44.substring(3, 4);
  const valorStr  = codigo44.substring(4, 15);

  if (produto !== '8') {
    erros.push('Produto inválido para conta de consumo.');
  }

  // Valida dígito verificador geral
  // Para linha digitável de 48 dígitos, o dígito geral não está disponível diretamente
  // então aceitamos sem validar o DV geral quando vier de conversão de 48 dígitos
  if (!linhaOriginal48) {
    const semDigito = codigo44.substring(0, 3) + codigo44.substring(4);
    let dvCalculado: number;
    if (realValor === '6' || realValor === '7') {
      dvCalculado = modulo10(semDigito);
    } else {
      dvCalculado = modulo11(semDigito);
    }
    if (String(dvCalculado) !== digitoGeral) {
      erros.push('Dígito verificador inválido.');
    }
  }

  // Para linha digitável de 48 dígitos, valida os DVs de cada bloco.
  // FEBRABAN permite que cada concessionária escolha módulo 10 ou 11,
  // e a direção (esquerda→direita ou direita→esquerda) varia por implementação.
  // Testamos todas as variantes e aceitamos se qualquer uma bater.
  if (linhaOriginal48) {
    const l = linhaOriginal48.replace(/\D/g, '');

    // Layout real FEBRABAN para linha digitável de concessionárias:
    // Bloco 1: P(0) S(1) R(2) DV_B1(3) VVVVVVVV(4-11) → dados = pos 0-2 + 4-11, DV = pos 3
    const bloco1Dados = l.substring(0, 3) + l.substring(4, 12); // 11 dígitos sem o DV
    const bloco1Dv    = parseInt(l.substring(3, 4), 10);
    // Bloco 2: CCCCCCCCCCC(12-22) DV_B2(23) → dados = pos 12-22, DV = pos 23
    const bloco2Dados = l.substring(12, 23);
    const bloco2Dv    = parseInt(l.substring(23, 24), 10);
    // Bloco 3: CCCCCCCCCCC(24-34) DV_B3(35) → dados = pos 24-34, DV = pos 35
    const bloco3Dados = l.substring(24, 35);
    const bloco3Dv    = parseInt(l.substring(35, 36), 10);
    // Bloco 4: CCCCCCCCCCC(36-46) DV_B4(47) → dados = pos 36-46, DV = pos 47
    const bloco4Dados = l.substring(36, 47);
    const bloco4Dv    = l.length > 47 ? parseInt(l.substring(47, 48), 10) : -1;

    // Testa se um bloco é válido com qualquer variante de módulo
    const blocoValido = (dados: string, dvEsperado: number): boolean => {
      if (dvEsperado < 0) return true; // bloco 4 opcional
      return (
        modulo10(dados)         === dvEsperado ||
        modulo10Esquerda(dados) === dvEsperado ||
        modulo11(dados)         === dvEsperado ||
        modulo11Esquerda(dados) === dvEsperado
      );
    };

    const dvErros: string[] = [];
    if (!blocoValido(bloco1Dados, bloco1Dv)) dvErros.push('bloco 1');
    if (!blocoValido(bloco2Dados, bloco2Dv)) dvErros.push('bloco 2');
    if (!blocoValido(bloco3Dados, bloco3Dv)) dvErros.push('bloco 3');
    if (!blocoValido(bloco4Dados, bloco4Dv)) dvErros.push('bloco 4');

    if (dvErros.length > 0) {
      erros.push(`Dígito verificador inválido (${dvErros.join(', ')}).`);
    }
  }

  // Extrai valor:
  // - Linha de 48 dígitos: layout real FEBRABAN → P(0) S(1) R(2) DV_B1(3) VVVVVVVV(4-11)
  //   Valor = posições 4 a 11 = 8 dígitos em centavos
  // - Código de barras de 44 dígitos: posições 4-14 (11 dígitos = centavos)
  let valor = 0;
  if (linhaOriginal48) {
    const l = linhaOriginal48.replace(/\D/g, '');
    // Valor = posições 4 a 11 (8 dígitos em centavos)
    const valorBruto = l.substring(4, 12);
    valor = parseInt(valorBruto, 10) / 100;
  } else {
    valor = parseInt(valorStr, 10) / 100;
  }

  // Segmentos conhecidos
  const segmentos: Record<string, string> = {
    '1': 'Prefeituras',
    '2': 'Saneamento',
    '3': 'Energia Elétrica e Gás',
    '4': 'Telecomunicações',
    '5': 'Órgãos Governamentais',
    '6': 'Carnes e Assemelhados',
    '7': 'Multas de Trânsito',
    '9': 'Uso Exclusivo do Banco',
  };

  const nomeBeneficiario = segmentos[segmento] || 'Concessionária / Serviço Público';

  return {
    valido: erros.length === 0,
    tipo: 'consumo',
    valor,
    vencimento: null,
    beneficiario: nomeBeneficiario,
    banco: '',
    erros,
  };
}

// ─── Função principal ─────────────────────────────────────────────────────────
export function validarCodigoBarras(codigo: string): ResultadoValidacao {
  const limpo = codigo.replace(/[\s.]/g, '').replace(/\D/g, '');

  if (limpo.length === 0) {
    return { valido: false, tipo: 'desconhecido', valor: 0, vencimento: null, beneficiario: '', banco: '', erros: ['Código vazio.'] };
  }

  // Linha digitável de boleto bancário (47 dígitos) → converte para 44
  if (limpo.length === 47) {
    const codigo44 = linhaDigitavelParaCodigoBarras(limpo);
    return validarBoleto(codigo44);
  }

  // Linha digitável de conta de consumo (48 dígitos) → converte para 44
  if (limpo.length === 48) {
    const produto = limpo.substring(0, 1);
    if (produto === '8') {
      const codigo44 = linhaDigitavelConsumoParaCodigoBarras(limpo);
      return validarContaConsumo(codigo44, limpo);
    }
    // Se não começa com 8, tenta como boleto com 48 dígitos (alguns layouts alternativos)
    return {
      valido: false,
      tipo: 'desconhecido',
      valor: 0,
      vencimento: null,
      beneficiario: '',
      banco: '',
      erros: [`Formato de 48 dígitos não reconhecido (produto: ${limpo.substring(0, 1)}).`],
    };
  }

  // Código de barras direto (44 dígitos)
  if (limpo.length === 44) {
    const produto = limpo.substring(0, 1);
    if (produto === '8') {
      return validarContaConsumo(limpo);
    }
    return validarBoleto(limpo);
  }

  // Comprimento inválido
  return {
    valido: false,
    tipo: 'desconhecido',
    valor: 0,
    vencimento: null,
    beneficiario: '',
    banco: '',
    erros: [`Comprimento inválido: ${limpo.length} dígitos (esperado 44, 47 ou 48).`],
  };
}
